/**
 * Pure helpers behind Tree: flattening for render, and the parent/child
 * check propagation that antd's `checkable` tree performs.
 */

export const flattenTree = (nodes = [], expandedKeys = [], depth = 0, parentKey = null) =>
  nodes.flatMap((node) => {
    const row = { ...node, depth, parentKey, hasChildren: Boolean(node.children?.length) }
    if (!row.hasChildren || !expandedKeys.includes(node.key)) return [row]
    return [row, ...flattenTree(node.children, expandedKeys, depth + 1, node.key)]
  })

export const collectKeys = (nodes = []) =>
  nodes.flatMap((node) => [node.key, ...collectKeys(node.children)])

const findNode = (nodes, key) => {
  for (const node of nodes) {
    if (node.key === key) return node
    const found = findNode(node.children ?? [], key)
    if (found) return found
  }
  return null
}

const parentMap = (nodes, parent = null, map = new Map()) => {
  for (const node of nodes) {
    map.set(node.key, parent)
    parentMap(node.children ?? [], node.key, map)
  }
  return map
}

/**
 * antd semantics: checking a node checks all its descendants, and a parent is
 * checked only when every child is. Partially checked parents land in
 * `halfCheckedKeys` instead.
 */
export const toggleChecked = (treeData, checkedKeys, key, checked) => {
  const node = findNode(treeData, key)
  if (!node) return { checked: checkedKeys, halfChecked: [] }

  const subtree = collectKeys([node])
  const next = new Set(checkedKeys)
  subtree.forEach((subKey) => (checked ? next.add(subKey) : next.delete(subKey)))

  const parents = parentMap(treeData)
  let cursor = parents.get(key)
  while (cursor) {
    const parent = findNode(treeData, cursor)
    const childKeys = (parent?.children ?? []).map((child) => child.key)
    if (childKeys.every((childKey) => next.has(childKey))) next.add(cursor)
    else next.delete(cursor)
    cursor = parents.get(cursor)
  }

  const halfChecked = []
  const walk = (nodes) => {
    for (const current of nodes) {
      if (current.children?.length) {
        const descendants = collectKeys(current.children)
        const some = descendants.some((descendant) => next.has(descendant))
        if (some && !next.has(current.key)) halfChecked.push(current.key)
        walk(current.children)
      }
    }
  }
  walk(treeData)

  return { checked: [...next], halfChecked }
}

export const filterTree = (nodes = [], predicate) =>
  nodes
    .map((node) => {
      const children = filterTree(node.children ?? [], predicate)
      if (predicate(node) || children.length > 0) return { ...node, children }
      return null
    })
    .filter(Boolean)

export { findNode }
